import Button from '@/components/atoms/Button'
import DatePicker from '@/components/atoms/DatePicker'
import Icon from '@/components/atoms/Icon'
import Modal from '@/components/atoms/Modal'
import Textarea from '@/components/atoms/Textarea'
import TimeRadio from '@/components/atoms/TimeRadio'
import { scheduleMentorshipSchema } from '@/schemas/mentorship.schema'
import mentorshipsService from '@/services/mentorshipsService'
import { notificationCreators } from '@/store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import inputIsValid from '@/utils/helpers/inputIsValid'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface ScheduleProps {
	disabledOnClose?: boolean
}

type Inputs = {
	goals?: string
}

export function Schedule({ disabledOnClose }: ScheduleProps) {
	const {
		auth: { user },
		modal: { modalName, modalInfo },
	} = useAppSelector((state) => state)

	const dispatch = useAppDispatch()

	const [selectedDate, setSelectedDate] = useState<string>()
	const [selectedTime, setSelectedTime] = useState('')
	const [currentMentorship, setCurrentMentorship] = useState<{
		name: string
		lastName: string
		profilePicture: string
	}>()
	const [minLenght, setMinLenght] = useState(true)

	const [confirmationModal, setConfirmationModal] = useState(false)

	const {
		execute: listMentorships,
		data: dataMentorships,
		isSuccess: isSuccessDataMentorships,
	} = mentorshipsService.listMutation()

	const {
		execute: createMentorship,
		isLoading,
		isSuccess,
	} = mentorshipsService.createMentorship()

	const {
		execute: rescheduleMentorship,
		isLoading: isLoadingReschedule,
		isSuccess: isSuccessReschedule,
	} = mentorshipsService.rescheduleMentorship()

	const mentor = {
		profilePicture: modalInfo?.profilePicture || undefined,
		companyPicture: modalInfo?.companyPicture || undefined,
	}

	function formatMentorName(name?: string, lastName?: string) {
		return `${name} ${lastName ? lastName[0].toUpperCase() : ''}.`
	}

	const name = modalInfo?.reschedule
		? formatMentorName(currentMentorship?.name, currentMentorship?.lastName)
		: formatMentorName(modalInfo?.name, modalInfo?.lastName)

	const {
		register,
		handleSubmit,
		formState: { errors, touchedFields },
		control,
		reset,
	} = useForm<Inputs>({
		mode: 'onBlur',
		context: { isRescheduling: !!modalInfo?.reschedule },
		resolver: yupResolver(scheduleMentorshipSchema),
	})

	const resetSchedule = () => {
		setConfirmationModal(false)
		setSelectedDate('')
		setSelectedTime('')
	}

	useEffect(() => {
		setSelectedTime('')
	}, [selectedDate])

	useEffect(() => {
		if (modalName === 'scheduleMentorshipModal') listMentorships()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalName])

	useEffect(() => {
		if (modalName === 'scheduleMentorshipModal' && isSuccessDataMentorships) {
			if (modalInfo?.mentorshipId) {
				setCurrentMentorship(
					dataMentorships?.mentorships?.find(
						(mentorship) =>
							Number(mentorship.id) === Number(modalInfo?.mentorshipId)
					)?.partner
				)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessDataMentorships && modalInfo?.mentorshipId && modalName])

	useEffect(() => {
		if (!modalName) resetSchedule()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalName])

	let price

	switch (modalInfo?.currentSessionDuration) {
		case 15: {
			price = 50
			break
		}
		case 30: {
			price = 100
			break
		}
		case 60: {
			price = 200
			break
		}
		default: {
			price = 100
			break
		}
	}

	const sessionsDates =
		modalInfo && modalInfo?.reschedule
			? modalInfo?.availabilities?.filter(
					({ hours }: { hours: Array<string> }) => hours?.length
				)
			: modalInfo?.calendar &&
				modalInfo?.calendar[modalInfo?.currentSessionDuration || 30]?.filter(
					({ hours }: { hours: Array<string> }) => hours?.length
				)

	const free = modalInfo?.freeMentorships

	const onSubmit = (data: Inputs) => {
		if (modalInfo?.id && selectedDate && selectedTime) {
			if (modalInfo?.reschedule) {
				rescheduleMentorship({
					mentorshipId: modalInfo.mentorshipId,
					date: dayjs(`${selectedDate} ${selectedTime}`).format(
						'YYYY-MM-DD HH:mm'
					),
				})
				return
			}

			createMentorship({
				date: dayjs(`${selectedDate} ${selectedTime}`).format(
					'YYYY-MM-DD HH:mm'
				),
				duration: String(modalInfo?.currentSessionDuration) || '30',
				mentorId: modalInfo.id,
				goals: data?.goals || '',
			})
		}
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(
				notificationCreators.show({
					message: 'Mentoria agendada',
				})
			)

			reset()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	useEffect(() => {
		if (isSuccessReschedule) {
			dispatch(
				notificationCreators.show({
					message: 'Mentoria remarcada',
				})
			)

			reset()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessReschedule])

	return (
		<Modal
			name='scheduleMentorshipModal'
			disabledOnClose={disabledOnClose}
			className='md:py-14 md:px-12 max-w-[500px]'
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				{(() => {
					if (confirmationModal) {
						if (modalInfo?.reschedule) {
							return (
								<div className='flex flex-col items-center gap-8'>
									<div>
										<p className='text-lg text-white font-medium leading-[140%]'>
											Confirmar remarcação?
										</p>
									</div>
									<div className='flex'>
										<div className='flex relative'>
											{(() => {
												if (mentor?.profilePicture) {
													return (
														<img
															src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.profilePicture}`}
															alt='Avatar Mentor'
															className='min-w-[68px] min-h-[68px] max-w-[68px] max-h-[68px] object-fill border-4 xl:border border-gray-100 rounded-full -ml-3'
															loading='lazy'
														/>
													)
												}

												return (
													<Icon
														name='default-avatar'
														width='68'
														height='68'
														className='min-w-[68px] min-h-[68px] max-w-[68px] max-h-[68px] object-fill border-4 xl:border border-gray-100 rounded-full -ml-3'
													/>
												)
											})()}
										</div>
									</div>
									<div className='flex flex-col gap-3'>
										<Dialog.Title
											as='h3'
											className='text-2xl font-medium leading-7 tracking-tight text-zinc-50'
										>
											Mentoria com {name}
										</Dialog.Title>
										<div className='flex flex-col gap-1'>
											{modalInfo?.dateMentorshipOld && (
												<p className='w-full text-md text-white/70 leading-[155%] max-w-[300px] first-letter:capitalize line-through'>
													{dayjs(modalInfo?.dateMentorshipOld)
														.locale('pt-br')
														.format('dddd, DD [de] MMMM [às] HH[h]mm')}
												</p>
											)}
											<p className='w-full text-lg text-white/70 leading-[155%] max-w-[400px] first-letter:capitalize'>
												{dayjs(selectedDate + selectedTime)
													.locale('pt-br')
													.format('dddd, DD [de] MMMM [às] HH[h]mm')}
											</p>
										</div>
									</div>
									<div className='w-full flex flex-col items-center gap-4'>
										<Button
											className='w-full max-w-[400px] h-[48px] rounded-full'
											isLoading={isLoadingReschedule}
											type='submit'
										>
											Confirmar Remarcação
										</Button>
										<button
											className='text-base text-white/70 leading-[140%] font-medium'
											onClick={resetSchedule}
										>
											Voltar
										</button>
									</div>
								</div>
							)
						}

						return (
							<div className='flex flex-col items-center gap-8'>
								<div>
									{(() => {
										if (free) {
											return (
												<p className='text-lg text-white/70 font-medium leading-[140%]'>
													Confirmar agendamento?
												</p>
											)
										}

										return (
											<div className='flex items-center gap-4'>
												<img
													src='/imgs/admin/awari-coin.png'
													alt='Emoji'
													className='w-[28px] h-[28px]'
												/>
												<p className='text-lg font-medium text-white/70 overflow-clip break-words'>
													Confirmar uso de {price} créditos?
												</p>
											</div>
										)
									})()}
								</div>
								<div className='flex'>
									<div className='flex relative'>
										{(() => {
											if (mentor?.profilePicture) {
												return (
													<img
														src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.profilePicture}`}
														alt='Avatar Mentor'
														className='min-w-[68px] min-h-[68px] max-w-[68px] max-h-[68px] object-fill border-4 xl:border border-gray-100 rounded-full -ml-3'
														loading='lazy'
													/>
												)
											}

											return (
												<Icon
													name='default-avatar'
													width='68'
													height='68'
													className='min-w-[68px] min-h-[68px] max-w-[68px] max-h-[68px] object-fill border-4 xl:border border-gray-100 rounded-full -ml-3'
												/>
											)
										})()}
									</div>
								</div>
								<div className='flex flex-col gap-1'>
									<Dialog.Title
										as='h3'
										className='text-2xl font-medium leading-7 tracking-tight text-zinc-50'
									>
										Mentoria com {name}
									</Dialog.Title>
									<p className='w-full text-lg text-white/70 leading-[155%] max-w-[400px] first-letter:capitalize'>
										{dayjs(selectedDate + selectedTime)
											.locale('pt-br')
											.format('dddd, DD [de] MMMM [às] HH[h]mm')}
									</p>
								</div>
								<div className='flex w-full'>
									<Controller
										control={control}
										name='goals'
										render={() => (
											<Textarea
												isValid={inputIsValid(
													errors.goals,
													touchedFields.goals
												)}
												{...register('goals')}
												minimalCharacters={20}
												onMinLenght={({ isDisabled }) => {
													setMinLenght(isDisabled)
												}}
												className='min-h-36 placeholder:font-medium'
												rows={8}
												placeholder='Descreva aqui qual seu objetivo com a mentoria. Essa informação é muito importante para o mentor se preparar para a conversa. Exemplos: se quiser revisar uma tarefa, inclua o link da entrega aqui. Para dúvidas de carreira, inclua seu perfil LinkedIn.'
											/>
										)}
									/>
								</div>
								<div className='w-full flex flex-col gap-4'>
									<Button
										className='w-full min-w-full h-[48px]'
										isLoading={isLoading}
										disabled={minLenght}
										type='submit'
									>
										Confirmar Agendamento
									</Button>
									<button
										className='text-base text-white/70 leading-[140%] font-bold'
										onClick={resetSchedule}
									>
										Voltar
									</button>
								</div>
							</div>
						)
					}

					return (
						<div className='flex flex-col items-center gap-4'>
							<div className='flex'>
								<div className='flex relative'>
									{(() => {
										if (mentor?.profilePicture) {
											return (
												<img
													src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.profilePicture}`}
													alt='Avatar Mentor'
													className='min-w-[92px] min-h-[92px] max-w-[92px] max-h-[92px] object-fill rounded-full'
												/>
											)
										}

										return (
											<Icon
												name='default-avatar'
												width='92'
												height='92'
												className='min-w-[92px] min-h-[92px] max-w-[92px] max-h-[92px] object-fill rounded-full'
											/>
										)
									})()}

									{(() => {
										if (mentor?.companyPicture) {
											return (
												<img
													src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.companyPicture}`}
													alt='Avatar Company Mentor'
													className='min-w-[44px] min-h-[44px] max-w-[44px] max-h-[44px] absolute -bottom-1 -right-4 object-fill rounded-full'
												/>
											)
										}

										return (
											<Icon
												name='default-avatar'
												width='44'
												height='44'
												className='min-w-[44px] min-h-[44px] max-w-[44px] max-h-[44px] absolute -bottom-1 -right-4 object-fill rounded-full'
											/>
										)
									})()}
								</div>
							</div>
							<div className='flex flex-col gap-2'>
								<Dialog.Title
									as='h3'
									className='text-xl font-medium leading-5 text-center tracking-tight text-gray-900 dark:text-[#F7F9FB]'
								>
									Mentoria com {name}
								</Dialog.Title>
								<p className='w-full text-base text-white/70 font-medium leading-[155%] max-w-[368px] first-letter:capitalize'>
									{user?.name ? `${user?.name},` : ''} escolha um dia abaixo
									para visualizar a disponibilidade de horários e agendar sua
									sessão de mentoria.
								</p>
							</div>
							<div className='flex flex-col items-center gap-10'>
								<div className='flex w-full max-w-[450px] justify-center items-center'>
									<DatePicker
										selectedDate={selectedDate}
										setSelectedDate={setSelectedDate}
										sessionsDates={sessionsDates}
									/>
								</div>

								{selectedDate && (
									<div className='flex flex-col gap-8 w-full'>
										<div className='w-full flex flex-col items-start gap-4'>
											<span className='text-lg font-medium leading-[140%] text-zinc-50'>
												Horários Disponíveis
											</span>
											<div>
												<TimeRadio
													selectedDate={selectedDate}
													setSelectedTime={setSelectedTime}
													sessionsDates={sessionsDates}
												/>
											</div>
										</div>

										<Button
											className='rounded-full'
											disabled={!selectedDate || !selectedTime}
											onClick={() => setConfirmationModal(true)}
										>
											Continuar
										</Button>
									</div>
								)}
							</div>
						</div>
					)
				})()}
			</form>
		</Modal>
	)
}
